import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useGetPatientQuery } from "store";
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Text,
    Box,
    Link as ChakraLink,
    Grid,
    GridItem,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Spinner,
    Center,
    Checkbox,
} from "@chakra-ui/react";
import moment from "moment";
import {
    MdMedicalServices,
    MdLocalPharmacy,
} from "react-icons/md";
import { PiTestTubeFill } from "react-icons/pi";
import jsPDF from "jspdf";

const PatientDetail = () => {
    const { id } = useParams();
    const { data: patientData, isLoading, error } = useGetPatientQuery(id);

    const today = new Date(); // Get today's date

    let birthDate, age;
    if (patientData) {
        birthDate = new Date(patientData.date_of_birth);
        // Calculate age considering month and day
        age = today.getFullYear() - birthDate.getFullYear();
        // Adjust age if birthday hasn't occurred this year
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
    }

    const Day = moment(patientData?.date_of_birth, "YYYY,MM,DD").toDate();

    const nextDay = Day.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    const regDay = moment(patientData?.reg_date, "YYYY,MM,DD").toDate();

    const nextRegDay = regDay.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    const base64Signature = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAALoAAABLBAMAAAA17oB+AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAtUExURf///5mZmURERO7u7qqqqgAAADMzMxkZGd3d3WZmZlVVVczMzIiIiHd3d7u7uwk2SxEAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAPmSURBVFjD7ZfPaxNBFMcn2TbZNO0SWlbqD8SDa8AfkCKhlNISKTmoVWqa6Zq0KUVIg4VCW9nGRiupupQeKgEN9SJUNFQKQi69eJAerNBLCUqw6KWgQUrxf3B+7Ga3v5uYvcg+yO7MsPnsd95782YWANNMM80000wzzRCr9xgIr+oxkm4dMRAOvC58nVlcbDSC7s+iSy2Mx+E7A+gL+BJ9DsAsXK44nI1g6UHcbAlXPL6ObnSRU6TNP6g03T6PLgIV7ZSSFaYXNhD1ltKZHqgwfcWjS3kuXGH6OfRbcqk9/zx4tvqqUYsu+2/0BPrli7SWwAIUJdjzQ4VL9/HNkigPzmKXD6qdz1CKnPaAp18gTSIwPRrAt3T0TFl0y210idA2J/SsNfWRZluATkdI8km8JrhAWXQbyhJLL2nOSg8RKe8jHT9JfS4IZBQTaxfgXeXQq3NqyszAs1ivtZ+MOyVcfqZHSJHzu0BNfzl0GUn14vrSAtfpSDRLbs3olUw8SbS7UT9eDh0vJvwGkHmp+uoeFR8CoOY2KdAsjoVSLJC3ho9O5z20BteqyxWwIXrPZ4mzkfYaXIocQVVP8Oj0bx46cX+qOJSnrqlOgXFAtMs53I8p0qUSkvMOoBOPaUPN1DW2AQ6rRNoXPKoPkf2GJVS6hJLyC9oQS4tNbTeJANJOXWUnmeWEJTgGTCCP4r8VsrpYkD2K6yUukl0s5XFkna3A1J4cdmsfug07slrnzRqyupgxsoS9riq62EAGOcgRiu/ev5hfV+MS9O1N9+KgWXQTZihBIK6QXQ6lPqPgOoXOnauq/Tja78Xz7qG9wpFQ0h0IuncvkdkLy1S7XZlWXehU+pE/p/936w03Ir/fSu5TqZF2nrzVqhNP9loQ81DtvBqSNIzothfm46obwqG1g1II0eeUcqidN0iOOinI6xsv8i4DuRieP1Eo3v10SHZOkJQnsQxrj+KC76B0uaNvV0QAM5WBwzcPP58g9KTS5EPFx7Gz7Ir2673a0zYS0/bvMTjRsQ+Q0+fOHHCqDmfToyrei8LK03G5s0t7WsAHiBOSeOnkfmrbBH2h4D1kqVK8MNagaERJmKGavfXa47jUbUriiwNc4h9PbqPrTtjcnPj6J24UUN5l6NmmOapNlU8xx8S3B0aydVuv4PPqM/iDJMYvXnGLqHmNJqJDLCq1hKcEsQGUYNW5lW1a2M1FCSbWtQFHcb9m0j1wOHt0NKbPj+8cYp7oe47itmKFgVI/IOrEQ7Zj51elwUUjpZ9hBzeO9lyTEKr0AVmzKilg4KdhGr4xDv4YXjAODgqTRn4yO42LqGmmmfZ/2l/GpdmVJAYFgwAAAABJRU5ErkJggg=='

    // Function to generate the PDF
    const generatePDF = (patientData) => {
        // Use Letter (215.9 x 279.4mm). Letter is the safer default for foolproof printing:
        // - Letter printer + Letter PDF: 1:1, perfect.
        // - A4 printer + Letter PDF: print dialogs default to "Fit to page", so it scales
        //   down a bit and stays fully visible.
        // The previous default (A4) was 17.6mm taller than Letter, so the signature block
        // anchored to the bottom of the page was clipped when printed on Letter without
        // "Fit to page" enabled.
        const doc = new jsPDF({ unit: "mm", format: "letter" });

        // Page geometry (Letter = 215.9 x 279.4mm). Derive everything from the page so
        // any future format change (e.g. switching back to A4) just works.
        const marginLeft = 20;
        const marginRight = 20;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const centerX = pageWidth / 2;
        const usableWidth = pageWidth - marginLeft - marginRight;
        const lineHeight = 7;

        //Set the font for the title
        doc.setFont("Times New Roman", "bold");

        // Title
        doc.setFontSize(16);
        doc.text("Central Medical Clinic", centerX, 20, { align: "center" });

        doc.setFontSize(14);
        doc.text("Paul's Ave, Kingstown", centerX, 27, { align: "center" });
        doc.text("St. Vincent and the Grenadines", centerX, 34, {
            align: "center",
        });
        doc.text("Tel: 1784-457-2603", centerX, 41, {
            align: "center",
        });

        //Reset the font for the entire document
        doc.setFont("Times New Roman", "normal");

        // Divider line
        doc.setDrawColor(0);
        doc.setLineWidth(1);
        doc.line(marginLeft, 50, pageWidth - marginRight, 50);

        // Writes wrapped text starting at y and returns the new y cursor below the block.
        const writeWrapped = (text, y) => {
            const lines = doc.splitTextToSize(String(text ?? ""), usableWidth);
            doc.text(lines, marginLeft, y);
            return y + lines.length * lineHeight;
        };

        // Writes a justified paragraph (last line stays left-aligned, per typography convention).
        // Manual justification: distribute the leftover space between words on each line.
        // We do this ourselves because jsPDF's built-in `align: "justify"` only kicks in
        // on multi-line array calls and uses its own internal line-height that doesn't
        // match our `lineHeight`, leading to inconsistent spacing.
        const writeJustified = (text, y) => {
            const lines = doc.splitTextToSize(String(text ?? ""), usableWidth);
            lines.forEach((rawLine, i) => {
                const isLast = i === lines.length - 1;
                const line = rawLine.trim();
                const words = line.split(/\s+/);
                const yPos = y + i * lineHeight;

                if (isLast || words.length < 2) {
                    doc.text(line, marginLeft, yPos);
                    return;
                }

                const wordsWidth = words.reduce(
                    (sum, w) => sum + doc.getTextWidth(w),
                    0
                );
                const gapWidth = (usableWidth - wordsWidth) / (words.length - 1);

                let x = marginLeft;
                words.forEach((word) => {
                    doc.text(word, x, yPos);
                    x += doc.getTextWidth(word) + gapWidth;
                });
            });
            return y + lines.length * lineHeight;
        };

        // Patient Info
        doc.setFontSize(14);
        let y = 65;
        y = writeWrapped(`Date: ${new Date().toLocaleDateString()}`, y);
        y = writeWrapped(`Name: ${patientData.name} ${patientData.surname}`, y + 3);
        y = writeWrapped(`Address: ${patientData.address}`, y + 3);
        y = writeWrapped(`Age: ${age}`, y + 3);

        // Certification heading
        y += 15;
        doc.setFont("Times New Roman", "bold");
        doc.text("TO WHOM IT MAY CONCERN:", marginLeft, y);

        // Certification body - three justified paragraphs separated by blank lines
        doc.setFont("Times New Roman", "normal");
        y += 10;

        const paragraphGap = lineHeight + 4;
        const fullName = `${patientData.name} ${patientData.surname}`;

        const certificationParagraph =
            `This is to certify that Mr./Mrs./Ms. ${fullName} of the above address has been ` +
            `medically examined and was found to be free of any chronic or acute infectious disease.`;
        y = writeJustified(certificationParagraph, y);

        y += paragraphGap;
        const opinionParagraph =
            `It is my opinion that Mr./Mrs./Ms. ${fullName} presents no significant public health risk.`;
        y = writeJustified(opinionParagraph, y);

        y += paragraphGap;
        y = writeJustified(
            "Any courtesies you could extend to him/her will be greatly appreciated.",
            y
        );

        // Signature block - sized so it always fits within Letter's 279.4mm height.
        // Vertical budget below the body:
        //   signatureHeight (30) + gapToUnderline (5) + gapToName (10) + bottomMargin (12)
        //   = 57mm reserved at the bottom.
        // The signature is anchored to the bottom of the page; if the body grew tall
        // enough to collide, it pushes the signature down only as much as is safe.
        const signatureHeight = 30;
        const signatureWidth = 50;
        const gapToUnderline = 5;
        const gapToName = 10;
        const bottomMargin = 12;
        const signatureBlockHeight =
            signatureHeight + gapToUnderline + gapToName + bottomMargin;

        const maxSignatureY = pageHeight - signatureBlockHeight;
        const preferredSignatureY = pageHeight - signatureBlockHeight - 3;
        const signatureY = Math.min(
            Math.max(y + 5, preferredSignatureY),
            maxSignatureY
        );

        doc.addImage(
            base64Signature,
            "PNG",
            (pageWidth - signatureWidth) / 2,
            signatureY,
            signatureWidth,
            signatureHeight
        );

        // Underline (centered, 70mm wide regardless of page size)
        const underlineY = signatureY + signatureHeight + gapToUnderline;
        const underlineWidth = 70;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(
            centerX - underlineWidth / 2,
            underlineY,
            centerX + underlineWidth / 2,
            underlineY
        );

        doc.text(
            "Dr. Roger Duncan M.D., MPH, MSc.",
            centerX,
            underlineY + gapToName,
            { align: "center" }
        );

        // Save the PDF
        doc.save(`${patientData.name}_${patientData.surname}_certificate.pdf`);
    
        
    };

    const formatHeight = (heightValue) => {
        if (!heightValue) return "N/A";
        const isFeetDecimal = heightValue % 1 !== 0 && heightValue < 20; // Heuristic for feet
        if (isFeetDecimal) {
            const feet = Math.floor(heightValue);
            const inches = Math.round((heightValue - feet) * 12);
            return `${feet}'${inches}"`;
        }
        const feet = Math.floor(heightValue / 12);
        const inches = Math.round(heightValue % 12);
        return `${feet}'${inches}"`;
    };

    const getTotalInches = (heightValue) => {
        if (!heightValue) return 0;
        const isFeetDecimal = heightValue % 1 !== 0 && heightValue < 20; // Heuristic for feet
        if (isFeetDecimal) {
            const feet = Math.floor(heightValue);
            const inches = Math.round((heightValue - feet) * 12);
            return feet * 12 + inches;
        }
        return heightValue;
    };

    // console.dir(nextRegDay);

    const navigate = useNavigate();

    const handleUpdateClick = () => {
        navigate(`/patients/${id}/update`);
    };

    const handleVisitsClick = () => {
        navigate(`/patients/${id}/visits`);
    };

    const handleLabsClick = () => {
        navigate(`/patients/${id}/labs`);
    };

    const handlePrescriptionsClick = () => {
        navigate(`/patients/${id}/prescriptions`);
    };

    if (isLoading) {
        return (
            <Center>
                <Spinner size="xl" />
            </Center>
        );
    }

    if (error) {
        return <Text>Error fetching patient details: {error.message}</Text>;
    }

    return (
        <Box mt={20} p={4}>
            <Grid pb={3} templateColumns="repeat(6, 1fr)" gap={4} width="100%">
                <GridItem>
                    <Button
                        colorScheme="teal"
                        leftIcon={<MdMedicalServices />}
                        onClick={(e) => {
                            handleVisitsClick();
                            e.stopPropagation();
                        }}
                    >
                        Visits
                    </Button>
                </GridItem>
                <GridItem>
                    <Button
                        colorScheme="teal"
                        leftIcon={<PiTestTubeFill />}
                        onClick={(e) => {
                            handleLabsClick();
                            e.stopPropagation();
                        }}
                    >
                        Labs
                    </Button>
                </GridItem>
                <GridItem>
                    <Button
                        colorScheme="teal"
                        leftIcon={<MdLocalPharmacy />}
                        onClick={(e) => {
                            handlePrescriptionsClick();
                            e.stopPropagation();
                        }}
                    >
                        Prescriptions
                    </Button>
                </GridItem>
            </Grid>

            <Card
                boxShadow="md"
                borderRadius="2xl"
                overflow="hidden"
                p={4}
                border="1px solid"
                borderColor="gray.300"
            >
                <CardHeader borderBottom="1px solid" borderColor="blue.200">
                    <Box textAlign="center" mb={4}>
                        <Text fontSize="2xl" fontWeight="bold">
                            Patient Form
                        </Text>
                    </Box>
                </CardHeader>

                <CardBody>
                    {/* GENERAL INFORMATION */}
                    <Box
                        overflowX="auto"
                        border="1px solid"
                        borderRadius="md"
                        borderColor="gray.300"
                    >
                        <Table
                            variant="striped"
                            colorScheme="blue"
                            size="md"
                            borderRadius="md"
                            overflow="hidden"
                        >
                            <Thead>
                                <Tr>
                                    <Th
                                        colSpan={11}
                                        bg="gray.200"
                                        textAlign="center"
                                        p={4}
                                        borderTopRadius="md"
                                    >
                                        <Text
                                            fontSize="2xl"
                                            fontFamily="Arial, sans-serif"
                                            fontWeight="bold"
                                        >
                                            GENERAL INFORMATION
                                        </Text>
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {/* Row 1 */}

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Surname</strong>
                                    </Td>
                                    <Td fontSize="md">{patientData.surname}</Td>
                                    <Td fontSize="md">
                                        <strong>Name</strong>
                                    </Td>
                                    <Td fontSize="md">{patientData.name}</Td>
                                    <Td fontSize="md">
                                        <strong>Age</strong>
                                    </Td>
                                    <Td fontSize="md">{age}</Td>
                                </Tr>

                                {/* Row 2 */}

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Sex</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.sex === "M"
                                            ? "Male"
                                            : patientData.sex === "F"
                                            ? "Female"
                                            : "Unknown"}
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Address</strong>
                                    </Td>
                                    <Td fontSize="md">{patientData.address}</Td>
                                    <Td fontSize="md">
                                        <strong>Telephone Number</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.telephone_number}
                                    </Td>
                                </Tr>

                                {/* Row 3 */}

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Nationality</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.nationality
                                            ? patientData.nationality
                                            : "NAD"}
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Date of Birth</strong>
                                        <input
                                            type="hidden"
                                            id="id_date_of_birth"
                                            value={nextDay}
                                        />
                                    </Td>
                                    <Td fontSize="md">{nextDay}</Td>
                                    <Td fontSize="md">
                                        <strong>Marital Status</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.marital_status === "S" &&
                                            "Single"}
                                        {patientData.marital_status === "M" &&
                                            "Married"}
                                        {patientData.marital_status === "D" &&
                                            "Divorced"}
                                        {patientData.marital_status === "W" &&
                                            "Widowed"}
                                        {patientData.marital_status === "N/A" &&
                                            "NAD"}
                                        {!(
                                            patientData.marital_status ===
                                                "S" ||
                                            patientData.marital_status ===
                                                "M" ||
                                            patientData.marital_status ===
                                                "D" ||
                                            patientData.marital_status ===
                                                "W" ||
                                            patientData.marital_status === "N/A"
                                        ) && "Unknown"}
                                    </Td>
                                </Tr>

                                {/* Row 4 */}

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Height</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {formatHeight(patientData.height)}
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Weight</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.weight} lbs
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>BMI</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {(() => {
                                            const totalInches = getTotalInches(
                                                patientData.height
                                            );
                                            if (totalInches > 0 && patientData.weight > 0) {
                                                return (
                                                    (patientData.weight * 703) /
                                                    (totalInches * totalInches)
                                                ).toFixed(2);
                                            }
                                            return "N/A";
                                        })()}
                                    </Td>
                                </Tr>

                                {/* Row 5 */}

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Next of Kin</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.next_of_kin}
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Relation of N.O.K</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.relation_nok}
                                    </Td>
                                    <Td fontSize="md">
                                        <strong># of N.O.K</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.telephone_nok}
                                    </Td>
                                </Tr>

                                {/* Row 6 */}

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>New Patient</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.is_new ? "Yes" : "No"}
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Occupation</strong>
                                    </Td>
                                    <Td fontSize="md">
                                        {patientData.occupation}
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Registration Date</strong>
                                    </Td>
                                    <Td fontSize="md">{nextRegDay}</Td>
                                </Tr>
                            </Tbody>
                        </Table>
                    </Box>

                    <br />

                    {/* MEDICAL HISTORY */}
                    <Box
                        overflowX="auto"
                        border="1px solid"
                        borderRadius="md"
                        borderColor="gray.300"
                    >
                        <Table
                            variant="striped"
                            colorScheme="blue"
                            size="md"
                            borderRadius="md"
                            overflow="hidden"
                        >
                            <Thead>
                                <Tr>
                                    <Th
                                        colSpan={11}
                                        bg="gray.200"
                                        textAlign="center"
                                        p={4}
                                        borderTopRadius="md"
                                    >
                                        <Text
                                            fontSize="2xl"
                                            fontFamily="Arial, sans-serif"
                                            fontWeight="bold"
                                        >
                                            MEDICAL HISTORY
                                        </Text>
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {/* Row 1 */}
                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Complaint:</strong>
                                    </Td>
                                    <Td colSpan={11} fontSize="md">
                                        {patientData.complaint
                                            ? patientData.complaint
                                            : "None"}
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Diabetes</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.diabetes}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Heart Murmur</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.heart_murmur}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Deep Vein Thrombosis</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.deep_vein_thrombosis
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>High Blood Pressure</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.high_blood_pressure
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Pneumonia</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.pneumonia}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Prostate</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.prostate_problems
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>High Cholesterol</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.high_cholesterol
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Pulmonary Embolism</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.pulmonary_embolism
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Anemia</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.anemia}
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Hypothyroidism</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.hypothyroidism
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Asthma</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.asthma}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Jaundice</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.jaundice}
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Hyperthyroidism</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.hyperthyroidism
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Bronchitis</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.emphysema_bronchitis
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Hepatitis</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.hepatitis}
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Cancer (type)</strong>
                                    </Td>
                                    <Td>{patientData.cancer_type}</Td>
                                    <Td fontSize="md">
                                        <strong>Stroke</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.stroke}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Ulcer</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.stomach_peptic_ulcer
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Systemic Lupus</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.lupus_erythematous
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Epilepsy</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.epilepsy}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Rheumatic Fever</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.rheumatic_fever
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>
                                            Congestive Heart Failure
                                        </strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.congestive_heart_failure
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Cataracts</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.cataracts}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Tuberculosis</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.tuberculosis}
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Angina</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.angina}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Kidney Disease</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.kidney_disease
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>HIV/AIDS</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.hiv_aids}
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Colitis</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={patientData.colitis}
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Kidney Stones</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.kidney_stones
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                    <Td fontSize="md">
                                        <strong>Psychiatric Illness</strong>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={
                                                patientData.psychiatric_illness
                                            }
                                            isReadOnly
                                        />
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td fontSize="md">
                                        <strong>Other</strong>
                                    </Td>
                                    <Td fontSize="md">{patientData.other}</Td>
                                </Tr>
                            </Tbody>
                        </Table>
                    </Box>

                    <br />

                    {/* ALLERGY */}
                    <Box
                        overflowX="auto"
                        border="1px solid"
                        borderRadius="md"
                        borderColor="gray.300"
                    >
                        <Table
                            variant="striped"
                            colorScheme="blue"
                            size="md"
                            borderRadius="md"
                            overflow="hidden"
                        >
                            <Tbody>
                                <Tr>
                                    <Th
                                        colSpan={11}
                                        bg="gray.200"
                                        textAlign="center"
                                        p={4}
                                        borderTopRadius="md"
                                    >
                                        <Text
                                            fontSize="2xl"
                                            fontFamily="Arial, sans-serif"
                                            fontWeight="bold"
                                        >
                                            ALLERGIES
                                        </Text>
                                    </Th>
                                </Tr>

                                <Tr>
                                    <Td
                                        colSpan={11}
                                        p={4}
                                        border="1px solid"
                                        borderColor="gray.300"
                                    >
                                        <Text
                                            fontSize="md"
                                            fontFamily="Arial, sans-serif"
                                        >
                                            {patientData.drug_allergy_details ? (
                                                <>
                                                    <strong>
                                                        Drug allergies:{" "}
                                                    </strong>{" "}
                                                    {
                                                        patientData.drug_allergy_details
                                                    }
                                                </>
                                            ) : (
                                                <>
                                                    <strong>
                                                        Drug allergies:{" "}
                                                    </strong>{" "}
                                                    None
                                                </>
                                            )}
                                        </Text>
                                    </Td>
                                </Tr>

                                <Tr>
                                    <Td
                                        colSpan={11}
                                        p={4}
                                        border="1px solid"
                                        borderColor="gray.300"
                                    >
                                        <Text
                                            fontSize="md"
                                            fontFamily="Arial, sans-serif"
                                        >
                                            {patientData.food_allergy_details ? (
                                                <>
                                                    <strong>
                                                        Food allergies:{" "}
                                                    </strong>{" "}
                                                    {
                                                        patientData.food_allergy_details
                                                    }
                                                </>
                                            ) : (
                                                <>
                                                    <strong>
                                                        Food allergies:{" "}
                                                    </strong>{" "}
                                                    None
                                                </>
                                            )}
                                        </Text>
                                    </Td>
                                </Tr>
                            </Tbody>
                        </Table>
                    </Box>
                </CardBody>

                <CardFooter p={4} borderTop="1px solid" borderColor="gray.300">
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                        <GridItem>
                            <ChakraLink
                                as={Link}
                                to="/patients"
                                color="blue.500"
                            >
                                Back to List
                            </ChakraLink>
                        </GridItem>
                        <GridItem textAlign="right">
                            <Button
                                colorScheme="blue"
                                onClick={(e) => {
                                    handleUpdateClick();
                                    e.stopPropagation();
                                }}
                            >
                                Update
                            </Button>
                        </GridItem>
                    </Grid>
                </CardFooter>
            </Card>

            <Button
                colorScheme="purple"
                variant="outline"
                leftIcon={<MdMedicalServices />}
                onClick={() => generatePDF(patientData)}
                mt={3}
            >
                Generate Medical Certificate
            </Button>
        </Box>
    );
};

export default PatientDetail;
